import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, IndianRupee, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const ISO9001LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Lead Auditor Certification ISO 9001",
    "description": "Advance your career with ISO 9001 Lead Auditor Certification. Gain in-depth QMS audit knowledge and internationally recognized credentials.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy"
    },
    "coursePrerequisites": "Quality management experience recommended",
    "educationalLevel": "Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "In-person",
      "duration": "5 days"
    }
  };

  const keyBenefits = [
    {
      title: "Global Recognition",
      description: "Lead auditor certification ISO 9001 is recognized internationally, allowing professionals to work with organizations and certification bodies worldwide."
    },
    {
      title: "Advanced Knowledge of ISO 9001:2015",
      description: "The certification provides in-depth understanding of:",
      points: [
        "Context of the organization",
        "Leadership and planning",
        "Risk-based thinking",
        "Performance evaluation and continual improvement"
      ]
    },
    {
      title: "Professional Auditing Skills",
      description: "You gain practical auditing competence in:",
      points: [
        "Audit planning and preparation",
        "Interview techniques",
        "Objective evidence collection",
        "Nonconformity reporting and follow-up"
      ]
    },
    {
      title: "Career Growth and Higher Earnings",
      description: "Certified ISO 9001 lead auditors often earn higher salaries and consulting fees compared to non-certified professionals."
    },
    {
      title: "Value Addition to Organizations",
      description: "For internal professionals, lead auditor certification ISO 9001 helps strengthen internal audits, reduce risks, and improve overall business performance."
    }
  ];

  const targetAudience = [
    "Quality Managers and Engineers",
    "Management Representatives",
    "Internal Auditors",
    "ISO Consultants",
    "Compliance and process improvement professionals",
    "Individuals aspiring to become third-party auditors"
  ];

  const careerOpportunities = [
    "ISO 9001 Lead Auditor",
    "Quality Management Consultant",
    "Certification Body Auditor",
    "Supplier Auditor",
    "Compliance Manager",
    "Independent QMS Auditor"
  ];

  const auditorCompetencies = [
    "Interpret ISO 9001:2015 requirements correctly",
    "Lead and manage audit teams",
    "Conduct internal, supplier, and certification audits",
    "Identify nonconformities and improvement opportunities",
    "Evaluate the effectiveness of a QMS"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Lead Auditor Certification ISO 9001 | QMS Course"
        description="Advance your career with ISO 9001 Lead Auditor Certification. Gain in-depth QMS audit knowledge and internationally recognized credentials."
        keywords="ISO 9001 lead auditor, lead auditor certification, QMS auditor training, ISO 9001:2015, quality management system audit, professional auditor certification"
        canonical="/Lead-Auditor-Certification-ISO-9001"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block bg-blue-500 px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              INTERNATIONALLY RECOGNIZED CERTIFICATION
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Lead Auditor Certification ISO 9001
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto">
              Advance your career with ISO 9001 Lead Auditor Certification. Gain in-depth QMS audit knowledge and internationally recognized credentials.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>5 Days Intensive Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6" />
                <span>Global Recognition</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iso-9001']} currentLevel="leadAuditor" />

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-9001" />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Introduction Section */}
        <section className="mb-16">
          <Card>
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                In today's competitive and quality-driven business environment, organizations worldwide rely on effective Quality Management Systems (QMS) to meet customer expectations and regulatory requirements. As ISO 9001 remains the most widely adopted quality standard globally, the demand for qualified auditors continues to rise. Lead Auditor Certification ISO 9001 is a globally recognized credential that validates a professional's ability to audit, manage, and improve quality management systems in line with ISO 9001:2015.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you are a quality professional, consultant, or auditor, achieving lead auditor certification can significantly enhance your career prospects and professional credibility.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* What Is Lead Auditor Certification */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              What Is Lead Auditor Certification ISO 9001?
            </h2>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Lead auditor certification ISO 9001 is a professional qualification that confirms an individual's competence to plan, conduct, report, and follow up on Quality Management System audits based on ISO 9001 requirements.
                </p>
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900">A certified ISO 9001 lead auditor is trained to:</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {auditorCompetencies.map((competency, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{competency}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-lg text-gray-700">
                  This certification is internationally accepted and essential for professionals aiming to work as third-party auditors or QMS consultants.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Why It's Important */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Why Lead Auditor Certification ISO 9001 Is Important
            </h2>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Organizations implementing ISO 9001 require skilled auditors to ensure compliance, effectiveness, and continual improvement. Lead auditor certification ISO 9001 assures employers and certification bodies that the auditor possesses the technical knowledge, auditing skills, and professional judgment required for quality audits.
                </p>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Key reasons to pursue the certification include:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Increasing global demand for ISO 9001 auditors</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Recognition across industries and countries</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Higher professional credibility and authority</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Career advancement and income growth</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Key Benefits */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              Key Benefits of Lead Auditor Certification ISO 9001
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {keyBenefits.map((benefit, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                        <Target className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold mb-3 text-gray-900">{index + 1}. {benefit.title}</h4>
                        <p className="text-gray-700 mb-3">{benefit.description}</p>
                        {benefit.points && (
                          <ul className="space-y-2">
                            {benefit.points.map((point, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-blue-600 mt-1">•</span>
                                <span className="text-gray-700">{point}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Who Should Attend */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              Who Should Attend Lead Auditor Certification ISO 9001 Training?
            </h3>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">This certification is ideal for:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {targetAudience.map((audience, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{audience}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Career Opportunities */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
              Career Opportunities After Lead Auditor Certification ISO 9001
            </h4>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Professionals holding lead auditor certification ISO 9001 can pursue roles such as:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  {careerOpportunities.map((opportunity, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                      <Award className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 font-semibold">{opportunity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Why Choose Accredited Training */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
              Why Choose Accredited Lead Auditor Certification ISO 9001 Training?
            </h4>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Selecting an accredited training provider ensures:
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg flex-shrink-0">
                      <Shield className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-2">Internationally recognized certification</h5>
                      <p className="text-gray-700">Credentials accepted by certification bodies worldwide</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg flex-shrink-0">
                      <Users className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-2">Qualified trainers with audit experience</h5>
                      <p className="text-gray-700">Learn from experienced professionals with real-world expertise</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg flex-shrink-0">
                      <BookOpen className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-2">Practical, audit-focused learning</h5>
                      <p className="text-gray-700">Hands-on exercises and real audit scenarios</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-2">Compliance with ISO 19011 guidelines</h5>
                      <p className="text-gray-700">Training aligned with international auditing standards</p>
                    </div>
                  </div>
                </div>
                <p className="text-lg text-gray-700 mt-6 font-semibold">
                  Accredited lead auditor certification ISO 9001 training ensures you are fully prepared to conduct professional audits.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Become a Certified ISO 9001 Lead Auditor?
                </h2>
                <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                  Join thousands of professionals who have advanced their careers with our internationally recognized certification program.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </div>
    </div>
  );
};
